import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiPost } from '../utils/api'; // assuming same helper you use in SignIn

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // On app load: restore session if valid
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const accessToken = localStorage.getItem('access_token');

    if (savedUser && accessToken) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (err) {
        console.error('Error parsing saved user data:', err);
        localStorage.clear();
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    setError('');
    setLoading(true);

    try {
      const response = await apiPost('/auth/signin', { email, password });
      const data = await response.json();

      if (response.ok) {
        const userData = {
          id: email,
          email,
          name: email.split('@')[0]
        };

        // Save to state + storage
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('token_type', data.token_type);

        return { success: true };
      } else {
        // Clear any stale session FIRST, before setting error
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_type');
        setUser(null);

        if (response.status === 401) {
          setError('Incorrect email or password');
        } else if (response.status === 400) {
          setError('Inactive user account');
        } else {
          setError(data.detail || 'Sign in failed. Please try again.');
        }

        return { success: false };
      }
    } catch (err) {
      console.error('Sign in error:', err);
      // Clear any stale session FIRST, before setting error
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('token_type');
      setUser(null);
      setError('Network error. Please check your connection and try again.');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.clear();
  }, []);

  const value = {
    user,
    login,
    logout,
    loading,
    error,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
